import escapeRegExp from 'lodash/escapeRegExp';

import { DEVELOPMENT_ENVS, REGIONAL_PROD_SUBDOMAINS } from 'constants/envs';

import { getWindowLocation } from './window';

export const validAltaAvigilonUrlRegEx = /^https:\/\/.*\.alta\.avigilon\.com/;

export const validDevelopmentUrlRegEx = /(http|https):\/\/(localhost)/;

const ALTA_DOMAIN = 'alta.avigilon.com';

/**
 * URL segments that sit in front of `alta.avigilon.com`, e.g. `dev` in
 * `platinum.dev.alta.avigilon.com` and `eu` in `control.eu.alta.avigilon.com`.
 *
 * Derived from constants/envs.ts so a new region is picked up here
 * automatically -- a missing segment makes getAltaDomain return undefined,
 * which silently degrades cookie scoping and app origin resolution.
 *
 * 'mine' is excluded: local development serves from localhost, not a subdomain.
 * Production has no segment at all, which is why the group is optional below.
 */
const ALTA_DOMAIN_SEGMENTS = [
  ...DEVELOPMENT_ENVS.filter((env) => env !== 'mine'),
  ...Object.values(REGIONAL_PROD_SUBDOMAINS),
];

const altaDomainSegmentPattern = ALTA_DOMAIN_SEGMENTS.map(
  (segment) => String.raw`${escapeRegExp(segment)}\.`,
).join('|');

const altaDomainRegex = new RegExp(
  `(?:${altaDomainSegmentPattern})?${escapeRegExp(ALTA_DOMAIN)}`,
);

/**
 * @description Checks if the origin url is using alta.avigilon.com finds the correct
 * domain for the current environment.
 * This method being used should only affect those using alta.avigilon.com. Openpath urls should be unaffected.
 * @returns the environment specific domain if using alta.avigilon.com in the url.
 */
export const getAltaDomain = () => {
  const { origin } = getWindowLocation();
  if (!origin) return undefined;

  return altaDomainRegex.exec(origin)?.[0];
};

/**
 * @returns an environment specific regex
 */
const getEnvDomainRegex = (env: string) =>
  new RegExp(String.raw`${escapeRegExp(env)}\.${escapeRegExp(ALTA_DOMAIN)}`);

/**
 * @description Checks domain for the current environment
 * This method being used should only affect non prod envrionments using alta.avigilon.com.
 * Openpath urls should be unaffected.
 * @returns the environment of the domain if using alta.avigilon.com in the url.
 */
export const getEnvironment = () => {
  const { origin } = getWindowLocation();
  if (!origin) return undefined;

  return ALTA_DOMAIN_SEGMENTS.find((env) =>
    getEnvDomainRegex(env).test(origin),
  );
};
